import { useEffect, useRef } from "react";
import { EPriceTabView } from "MVVM/@types";
import { analytics } from "MVVM/Analytics";
import { useRecoilValue } from "recoil";
import { useUser } from "MVVM/Hooks/useUser";
import { useIsInternalAccount } from "MVVM/Hooks/useIsInternalAccount";
import { useUnlimitedOnlyPaywall } from "MVVM/Hooks/useUnlimitedOnlyPaywall";
import { useResolvedPaymentProvider } from "MVVM/Hooks/useResolvedPaymentProvider";
import { PromoSession } from "MVVM/State/promoSession";
import { getUnlimitedPriceOffer } from "MVVM/State/unlimitedPriceExperiment";

import {
  BASIC_PLAN_EXPERIMENT,
  BASIC_PLAN_LOOKUP,
  basicPlanVariant,
  experimentTiers,
} from "MVVM/State/basicPlanExperiment";

// Shared offer for all upgrade surfaces. The previous price A/B is retired;
// Unlimited is now fixed, and only the presence of Basic is randomized.
export const useNewSubscriberOffer = (
  source: string,
  { trackExposure = true }: { trackExposure?: boolean } = {},
) => {
  const { allstarUser } = useUser();
  const { unlimitedOnly } = useUnlimitedOnlyPaywall();
  const { provider } = useResolvedPaymentProvider();
  const internal = useIsInternalAccount();
  const promoSession = useRecoilValue(PromoSession);
  const promo = promoSession?.activeUpgradePagePromo;
  const offer = getUnlimitedPriceOffer({
    user: allstarUser?.user,
    unlimitedOnly,
    provider,
    internal,
    hasPromo:
      !promoSession.loaded ||
      !!(
        promo?.name ||
        promo?.pricing?.monthly?.platinum ||
        promo?.pricing?.annual?.platinum ||
        promo?.pricing?.monthly?.essentials ||
        promo?.pricing?.annual?.essentials
      ),
    // Launch with the merged frontend; an explicit false remains the build-time kill switch.
    enabled: process.env.REACT_APP_NEW_SUBSCRIBER_PRICING !== "false",
  });
  const userId = allstarUser?.user?._id;
  const variant =
    offer && userId && process.env.REACT_APP_BASIC_PLAN_TEST !== "false"
      ? basicPlanVariant(userId)
      : undefined;
  const exposure = useRef<string>();
  useEffect(() => {
    if (!trackExposure || !variant || !userId || !offer) return;
    const key = `${userId}:${variant}:${source}`;
    if (exposure.current === key) return;
    exposure.current = key;
    analytics.track("Basic Plan Experiment Viewed", {
      experiment: BASIC_PLAN_EXPERIMENT,
      plan_variant: variant,
      source,
      provider: "stripe",
      account_id: userId,
      offered_plans: [...experimentTiers(variant)],
      unlimited_monthly_lookup_key: offer.stripeLookup[EPriceTabView.Monthly],
      unlimited_annual_lookup_key: offer.stripeLookup[EPriceTabView.Annual],
      basic_monthly_lookup_key:
        variant === "treatment" ? BASIC_PLAN_LOOKUP : undefined,
    });
  }, [userId, variant, source, offer, trackExposure]);
  return offer ? { ...offer, planVariant: variant } : null;
};
